/**
 * Branded PNL card for wins — rendered to a PNG the player can post to X.
 * Pure client-side canvas, no external services.
 */

export type WinCard = {
  amountEth: string; // "+0.0500"
  multiplier: string; // "10"
  totalEth: string; // "0.0050"
  handle?: string; // X handle, without @
  avatarUrl?: string; // avatar data URI / URL
};

const W = 1080;
const H = 1080;

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export async function buildWinCard(card: WinCard): Promise<Blob> {
  try {
    await (document as unknown as { fonts?: { ready: Promise<unknown> } }).fonts?.ready;
  } catch {
    /* fonts optional */
  }

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;

  const lime = "#00c805";
  const limeBright = "#29ff5b";
  const ink = "#eef5ea";
  const muted = "#7d8f80";

  // background
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, "#0b120c");
  bg.addColorStop(1, "#060806");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // subtle lime glow bottom-right
  const glow = ctx.createRadialGradient(W, H, 40, W, H, 700);
  glow.addColorStop(0, "rgba(0,200,5,0.22)");
  glow.addColorStop(1, "rgba(0,200,5,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  const pad = 80;

  // brand mark: the wheel logo (public/wheel-logo.png) — fall back to a small
  // drawn colour wheel if the asset isn't present.
  const logoSize = 84;
  const logo = await loadImage("/wheel-logo.png");
  if (logo) {
    ctx.drawImage(logo, pad, pad - 8, logoSize, logoSize);
  } else {
    const cx = pad + 42;
    const cy = pad + 34;
    const R = 40;
    const COLORS = [
      "#00C805", "#E11D48", "#F59E0B", "#0EA5E9", "#7C3AED",
      "#FF6B4A", "#14B8A6", "#EAB308", "#D946EF", "#6366F1",
    ];
    for (let i = 0; i < 10; i++) {
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, R, (i / 10) * Math.PI * 2 - Math.PI / 2, ((i + 1) / 10) * Math.PI * 2 - Math.PI / 2);
      ctx.closePath();
      ctx.fillStyle = COLORS[i];
      ctx.fill();
    }
    ctx.beginPath();
    ctx.arc(cx, cy, 12, 0, Math.PI * 2);
    ctx.fillStyle = "#0a0f0a";
    ctx.fill();
  }

  ctx.fillStyle = ink;
  ctx.font = "700 40px Silkscreen, monospace";
  ctx.textBaseline = "middle";
  ctx.fillText("WHEEL", pad + logoSize + 22, pad + 34);

  // "YOU WON"
  ctx.fillStyle = muted;
  ctx.font = "700 30px 'JetBrains Mono', monospace";
  ctx.textBaseline = "alphabetic";
  ctx.fillText("YOU WON", pad, 300);

  // big lime win bar
  const barY = 330;
  const barH = 150;
  roundRect(ctx, pad, barY, W - pad * 2, barH, 16);
  const barGrad = ctx.createLinearGradient(pad, barY, pad, barY + barH);
  barGrad.addColorStop(0, limeBright);
  barGrad.addColorStop(1, lime);
  ctx.fillStyle = barGrad;
  ctx.fill();
  ctx.fillStyle = "#04240a";
  ctx.font = "700 92px Silkscreen, monospace";
  ctx.textBaseline = "middle";
  ctx.fillText(`${card.amountEth} ETH`, pad + 34, barY + barH / 2 + 4);

  // stats
  const rows: [string, string][] = [
    ["MULTIPLIER", `${card.multiplier}×`],
    ["RESULT", "GREEN HIT ✓"],
    ["TOTAL BET", `${card.totalEth} ETH`],
  ];
  let ry = 590;
  ctx.textBaseline = "alphabetic";
  for (const [label, value] of rows) {
    ctx.fillStyle = muted;
    ctx.font = "500 34px 'JetBrains Mono', monospace";
    ctx.textAlign = "left";
    ctx.fillText(label, pad, ry);
    ctx.fillStyle = label === "MULTIPLIER" || label === "RESULT" ? limeBright : ink;
    ctx.font = "700 34px 'JetBrains Mono', monospace";
    ctx.textAlign = "right";
    ctx.fillText(value, W - pad, ry);
    ry += 66;
  }
  ctx.textAlign = "left";

  // player avatar + handle
  const footY = 890;
  if (card.handle) {
    const av = card.avatarUrl ? await loadImage(card.avatarUrl) : null;
    const size = 76;
    if (av) {
      ctx.save();
      roundRect(ctx, pad, footY, size, size, 16);
      ctx.clip();
      ctx.drawImage(av, pad, footY, size, size);
      ctx.restore();
      ctx.strokeStyle = "rgba(255,255,255,0.15)";
      ctx.lineWidth = 2;
      roundRect(ctx, pad, footY, size, size, 16);
      ctx.stroke();
    }
    ctx.fillStyle = ink;
    ctx.font = "700 44px 'JetBrains Mono', monospace";
    ctx.textBaseline = "middle";
    ctx.fillText(`@${card.handle}`, pad + (av ? size + 24 : 0), footY + size / 2);
  }

  // footer
  ctx.fillStyle = muted;
  ctx.font = "500 30px 'JetBrains Mono', monospace";
  ctx.textBaseline = "alphabetic";
  ctx.fillText("$WHEEL · the first zero-house-edge app", pad, H - pad + 6);

  return await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("card render failed"))), "image/png")
  );
}

/** The tweet copy for a win. */
export function winText(card: WinCard): string {
  return `Just won ${card.amountEth} ETH at ${card.multiplier}× on @ZeroEdgeWheel — the first zero-house-edge app. Provably fair, on-chain. 🟢`;
}

/** Build the card as a PNG data URL (for API posting). */
export async function buildWinCardDataUrl(card: WinCard): Promise<string> {
  const blob = await buildWinCard(card);
  return await new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(new Error("card encode failed"));
    r.readAsDataURL(blob);
  });
}

/**
 * Fallback share (no write token): opens the X compose tab IMMEDIATELY
 * (synchronously with the click, so it isn't popup-blocked) and downloads the
 * PNG so the user attaches it. On mobile with file-share support, uses the
 * native sheet which includes the image automatically.
 */
export async function shareWinToX(card: WinCard): Promise<void> {
  const text = winText(card);

  const nav = navigator as Navigator & {
    canShare?: (data?: unknown) => boolean;
    share?: (data: unknown) => Promise<void>;
  };
  const canFileShare =
    typeof nav.canShare === "function" &&
    typeof nav.share === "function" &&
    // heuristic: only trust the native sheet on touch devices (mobile), where
    // it reliably surfaces the X app WITH the image. On desktop it's the OS
    // share dialog, which confuses users — use the compose tab there instead.
    matchMedia("(pointer: coarse)").matches;

  // Open the X compose window NOW, in the click gesture, so it isn't blocked.
  // (We may replace this with the native sheet below on mobile.)
  const xWin = canFileShare
    ? null
    : window.open(
        `https://x.com/intent/tweet?text=${encodeURIComponent(text)}`,
        "_blank",
        "noopener"
      );

  let blob: Blob;
  try {
    blob = await buildWinCard(card);
  } catch {
    return; // compose tab already opened; nothing to attach
  }

  if (canFileShare) {
    const file = new File([blob], "wheel-win.png", { type: "image/png" });
    if (nav.canShare!({ files: [file] })) {
      try {
        await nav.share!({ files: [file], text });
        return;
      } catch {
        // cancelled/failed — fall through to open X + download
      }
    }
    window.open(`https://x.com/intent/tweet?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  }

  // download the card so it can be attached to the compose window
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "wheel-win.png";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  if (xWin) xWin.focus?.();
}
